/**
 * API Configuration Module
 *
 * This module configures AWS Amplify API clients for the application's GraphQL endpoints.
 * It sets up clients for both the DimSum and Deck Management APIs with appropriate
 * authentication and endpoint configurations.
 */

import { CommonPublicClientOptions } from '@aws-amplify/api-graphql';
import { RequestOptions } from '@aws-amplify/data-schema/runtime';
import { Sha256 } from '@aws-crypto/sha256-js';
import { AssumeRoleCommand, STSClient } from '@aws-sdk/client-sts';
import { HttpRequest } from '@aws-sdk/protocol-http';
import { SignatureV4 } from '@smithy/signature-v4';
import { getAurigaSettings } from '@utils/cookies';
import { extractGraphqlOperationName } from '@utils/graphqlOperationName';
import { generateClient } from 'aws-amplify/api';
import { fetchAuthSession } from 'aws-amplify/auth';
import { isNil } from 'lodash';
import { ApiClients, AurigaSubscriptionClient } from 'src/types/ApiProvider.types';
import { AurigaSettings } from 'types/Auth.types';

/**
 * Interface for the persisted cross-account credentials used in subscriptions
 */
interface CredentialsCache {
  accessKeyId: string;
  secretAccessKey: string;
  sessionToken: string;
}

// Credentials cache for cross-account subscription access
let credentialsCache: CredentialsCache | null = null;
let credentialsExpiration: Date | null = null;

/**
 * Gets valid cross-account credentials for AppSync subscriptions with automatic refresh
 *
 * Implements credential caching with a 5-minute refresh buffer before expiration.
 * Credentials are valid for 1 hour but refresh at 55 minutes to ensure continuity.
 *
 * @returns Promise resolving to cached or fresh cross-account credentials
 * @throws Error if STS assume role operation fails
 */
/**
 * Gets valid cross-account credentials for AppSync subscriptions with automatic refresh
 *
 * Implements credential caching with a 5-minute refresh buffer before expiration.
 * Credentials are valid for 1 hour but refresh at 55 minutes to ensure continuity.
 *
 * @returns Promise resolving to cached or fresh cross-account credentials
 * @throws Error if STS assume role operation fails
 */
const getValidSubscriptionCredentials = async (): Promise<CredentialsCache> => {
  const now = new Date();

  if (
    isNil(credentialsCache) ||
    isNil(credentialsExpiration) ||
    credentialsExpiration.getTime() - now.getTime() < 300000 // refreshes 5 minutes before defined expiry
  ) {
    // Get session and Auriga settings for necessary values
    const session = await fetchAuthSession();
    const aurigaSettings = getAurigaSettings();

    // Creates the STS client and assumes the expected Subscription Role in the producer account
    const stsClient = new STSClient({
      region: aurigaSettings.region,
      credentials: session.credentials,
    });

    const assumeRoleCommand = new AssumeRoleCommand({
      RoleArn: aurigaSettings.apiParams.deckSubscriptionRoleArn,
      RoleSessionName: 'AppSyncSubscriptionSession',
      ExternalId: 'appsync-subscription-access',
      DurationSeconds: 3600, // 1 hour
    });

    const response = await stsClient.send(assumeRoleCommand);

    credentialsCache = {
      accessKeyId: response.Credentials!.AccessKeyId!,
      secretAccessKey: response.Credentials!.SecretAccessKey!,
      sessionToken: response.Credentials!.SessionToken!,
    };
    credentialsExpiration = new Date(response.Credentials!.Expiration!);
  }

  return credentialsCache;
};

/**
 * Creates a SignatureV4 signer with cross-account credentials for AppSync operations
 *
 * @param aurigaSettings - Configuration containing region and API endpoints
 * @returns Promise resolving to the configured SignatureV4 signer instance
 */
const createAppSyncSigner = async (aurigaSettings: AurigaSettings) => {
  const credentials = await getValidSubscriptionCredentials();

  return new SignatureV4({
    credentials: {
      accessKeyId: credentials.accessKeyId,
      secretAccessKey: credentials.secretAccessKey,
      sessionToken: credentials.sessionToken,
    },
    region: aurigaSettings.region,
    service: 'appsync',
    sha256: Sha256,
  });
};

/**
 * Creates an HTTP request object for AppSync operations with standard headers
 *
 * @param endpoint - Target URL endpoint for the request
 * @param body - Request body content as string
 * @param customHeaders - Optional additional headers to merge with defaults
 * @returns HTTP request configuration object
 */
const createAppSyncRequest = (endpoint: URL, body: string, customHeaders: Record<string, string> = {}) => {
  return {
    method: 'POST',
    protocol: endpoint.protocol,
    hostname: endpoint.hostname,
    path: endpoint.pathname,
    headers: {
      accept: 'application/json, text/plain, */*',
      'content-encoding': 'amz-1.0',
      'content-type': 'application/json; charset=UTF-8',
      host: endpoint.hostname,
      ...customHeaders,
    },
    body,
  };
};

/**
 * Base64 encodes an object for WebSocket URL parameters
 *
 * Used specifically for encoding signed headers and payload data in AppSync WebSocket URLs.
 *
 * @param obj - Object to encode (typically headers or payload data)
 * @returns Base64 encoded string representation of the object
 */
const base64Encode = (obj: Record<string, unknown>): string => {
  return btoa(JSON.stringify(obj));
};

/**
 * Validates and parses WebSocket message data
 *
 * @param data - Raw WebSocket message data
 * @returns Validation result with the parsed message if valid
 */
const validateWebSocketMessage = (data: string): { isValid: boolean; message?: Record<string, unknown> } => {
  try {
    const message = JSON.parse(data);
    if (!message || typeof message !== 'object' || Array.isArray(message) || !message.type) {
      return { isValid: false };
    }
    return { isValid: true, message };
  } catch {
    return { isValid: false };
  }
};

/**
 * Creates the necessary web socket url with signed header for the subscription connection
 * @param aurigaSettings
 */
const createSignedWebSocketUrl = async (aurigaSettings: AurigaSettings) => {
  const connectEndpoint = `${aurigaSettings.apiParams.deckManagementGraphqlEndpoint}/connect`;

  // Sign the credentials to be used in the WebSocket
  const signer = await createAppSyncSigner(aurigaSettings);

  const endpoint = new URL(connectEndpoint);
  const requestData = JSON.stringify({});

  const startRequest = createAppSyncRequest(endpoint, requestData, {
    accept: 'application/json', // Override for WebSocket connection
  });

  const signedParams = await signer.sign(startRequest);

  // Validate and construct WebSocket endpoint URL
  const graphqlEndpoint = aurigaSettings.apiParams.deckManagementGraphqlEndpoint;
  if (!graphqlEndpoint.startsWith('https://') || !graphqlEndpoint.includes('.appsync-api.')) {
    throw new Error('Invalid AppSync endpoint URL');
  }

  // Parse URL and construct WebSocket endpoint safely
  const graphqlEndpointUrl = new URL(graphqlEndpoint);
  const realtimeEndpoint = `wss://${graphqlEndpointUrl.hostname.replace('appsync-api', 'appsync-realtime-api')}${graphqlEndpointUrl.pathname}`;

  return `${realtimeEndpoint}?header=${base64Encode(signedParams.headers)}&payload=${base64Encode({})}`;
};

/**
 * Creates a signed subscription message for AppSync WebSocket connections
 * @param options - GraphQL subscription options containing query and variables
 * @param options.query - The GraphQL subscription query string
 * @param options.variables - Optional variables for the subscription query
 * @param customHeaders - Custom headers to include in the signed request (e.g., X-Amzn-Requester)
 * @param aurigaSettings - Auriga configuration settings containing API endpoints and region
 * @returns Promise resolving to a WebSocket subscription message with signed authorization headers
 */
const createSubscriptionMessage = async (
  options: { query: string; variables?: Record<string, unknown> },
  customHeaders: Record<string, string>,
  aurigaSettings: AurigaSettings,
) => {
  const requestData = {
    query: options.query,
    variables: options.variables || {},
  };

  const endpoint = new URL(aurigaSettings.apiParams.deckManagementGraphqlEndpoint);
  const signer = await createAppSyncSigner(aurigaSettings);
  const request = new HttpRequest(createAppSyncRequest(endpoint, JSON.stringify(requestData), customHeaders));
  const signedParams = await signer.sign(request);

  return {
    id: crypto.randomUUID(),
    type: 'start',
    payload: {
      data: JSON.stringify({
        query: requestData.query,
        variables: requestData.variables || {},
      }),
      extensions: {
        authorization: signedParams.headers,
      },
    },
  };
};

/**
 * Gets the signed-in user's Cognito ID token. Its custom:USERNAME claim is the alias the Deck Management
 * API authorizes as. Amplify's 'oidc' mode would send the access token, which lacks that claim.
 *
 * @returns Promise resolving to the ID token as a JWT string
 * @throws Error if there is no signed-in user
 */
const getIdToken = async (): Promise<string> => {
  const idToken = (await fetchAuthSession()).tokens?.idToken?.toString();
  if (!idToken) {
    throw new Error('No Cognito ID token for the signed-in user.');
  }
  return idToken;
};

// To ensure apiClients is only initialized once
let apiClients: ApiClients | null = null;

// To add visibility to headers of potential Edit locks that exist
type LockTimestampProvider = () => string | undefined;
let getCurrentLockTimestamp: LockTimestampProvider | null = null;

/**
 * Sets the function to retrieve the current Edit lock timestamp
 * Used where needed (such as DeckContext) to provide Lock state for use in API headers
 *
 * @param provider - Function that returns the current lock timestamp or undefined
 */
export const setLockedTimestampProvider = (provider: LockTimestampProvider): void => {
  getCurrentLockTimestamp = provider;
};

/**
 * Configures API clients for the application
 *
 * This function initializes and configures AWS Amplify API clients for the
 * application's GraphQL endpoints. It creates separate clients for:
 *
 * - Deck Management API - For managing financial decks and pages
 * - DimSum API - For dimensional data management and queries
 *
 * Each client is configured with the appropriate endpoint and authentication
 * settings from their respective config files.
 *
 * @param {string} authUser - The authenticated user identity
 * @returns {Object} Object containing configured API clients
 * @returns {Object} returns.deckManagementClient - Client for Deck Management API
 * @returns {Object} returns.dimSumClient - Client for DimSum API
 *
 * @example
 * // Initialize API clients
 * const { dimSumClient, deckManagementClient } = configureApiClients();
 *
 * // Use the clients for API calls
 * const result = await dimSumClient.graphql({
 *   query: myQuery,
 *   variables: myVariables
 * });
 */
const configureApiClients = (authUser: string): ApiClients => {
  // Return the api clients if already generated
  if (apiClients) {
    return apiClients;
  }

  // Parse auriga-settings cookie for necessary AppSync values
  const aurigaSettings = getAurigaSettings();
  if (!aurigaSettings) {
    throw new Error('Unexpected error configuring API clients. Please ensure proper API configuration exists.');
  }

  /**
   * Uses known Authenticated User identity and configures a custom header to pass the required User alias
   * to the backend as an 'X-Amzn-Requester' header for all client requests.
   * Also includes the GraphQL operation name in the 'X-GraphQL-Operation-Name' header (useful when looking for graphql
   * requests in the Browser's Network Inspector).
   */
  const customHeaders = async (requestOptions?: RequestOptions): Promise<Record<string, string>> => {
    const operationName = extractGraphqlOperationName(requestOptions?.queryString);

    const headers: Record<string, string> = {
      'X-Amzn-Requester': authUser,
      'X-GraphQL-Operation-Name': operationName,
    };

    // Add Lock Timestamp if available
    if (getCurrentLockTimestamp) {
      const lockTimestamp = getCurrentLockTimestamp();
      if (lockTimestamp) {
        headers['X-Locked-Timestamp'] = lockTimestamp;
      }
    }

    return headers;
  };

  /**
   * Deck Management headers: the ID token goes in Authorization, and the API authorizes as its verified
   * custom:USERNAME claim. Client headers are applied after Amplify's own auth header, so this replaces the
   * access token 'oidc' mode would send.
   */
  const deckManagementHeaders = async (requestOptions?: RequestOptions): Promise<Record<string, string>> => ({
    ...(await customHeaders(requestOptions)),
    Authorization: await getIdToken(),
  });

  // Default client for Deck Management API, authenticated by the user's Cognito ID token (the API's OIDC mode)
  const deckManagementClient = generateClient({
    endpoint: aurigaSettings.apiParams.deckManagementGraphqlEndpoint,
    region: aurigaSettings.region,
    headers: deckManagementHeaders,
    authMode: 'oidc' as const,
  });

  const dimSumClient = generateClient({
    endpoint: aurigaSettings.apiParams.dimSumGraphqlEndpoint,
    // Will add aurigaSettings.authenticationType as value of authMode once iam works and is tested in all env.
    authMode: 'identityPool' as const,
    region: aurigaSettings.region,
    headers: customHeaders,
  } as CommonPublicClientOptions);

  /**
   * Creates a custom subscription client that handles cross-account WebSocket connections
   *
   * This client automatically detects subscription queries and routes them through a custom
   * WebSocket implementation that uses cross-account STS credentials for authentication.
   * Non-subscription queries and mutations are rejected with an error.
   *
   * Maintains a single shared WebSocket connection and multiplexes all subscriptions over it
   * using the AppSync graphql-ws protocol. Each .subscribe() call registers a subscription
   * with a unique ID; incoming messages are routed to the correct observer by that ID.
   * The connection is lazily established on the first subscribe and torn down when the last
   * subscription unregisters.
   *
   * @returns {AurigaSubscriptionClient} Custom client with automatic credential refresh and WebSocket management
   */
  const createAurigaSubscriptionClient = (): AurigaSubscriptionClient => {
    let websocket: WebSocket | null = null;
    let connectionReady: Promise<void> | null = null;
    let refreshTimeout: NodeJS.Timeout | null = null;
    const subscriptions = new Map<
      string,
      {
        observer: { next?: (value: { data: unknown }) => void; error?: (error: Error) => void };
        options: { query: string; variables?: Record<string, unknown> };
      }
    >();

    /** Tears down the WebSocket and notifies all active observers of the error */
    const teardown = (error?: Error) => {
      if (refreshTimeout) {
        clearTimeout(refreshTimeout);
        refreshTimeout = null;
      }
      if (websocket) {
        websocket.close();
        websocket = null;
      }
      connectionReady = null;
      if (error) {
        subscriptions.forEach(({ observer }) => observer.error?.(error));
        subscriptions.clear();
      }
    };

    /**
     * Establishes the shared WebSocket connection.
     * Resolves once connection_ack is received from AppSync.
     * Subsequent calls return the same promise while a connection attempt is in flight.
     */
    const connect = (): Promise<void> => {
      if (connectionReady) return connectionReady;

      connectionReady = new Promise<void>((resolve, reject) => {
        (async () => {
          try {
            const wsUrl = await createSignedWebSocketUrl(aurigaSettings);
            console.debug('Connecting to WebSocket with cross-account credentials for Auriga Subscriptions...');

            const ws = new WebSocket(wsUrl, 'graphql-ws');
            websocket = ws;

            // Calculate exact timeout for auto-credential refresh (5 min before expiry)
            if (credentialsExpiration) {
              const msUntilRefresh = credentialsExpiration.getTime() - Date.now() - 300000;
              if (msUntilRefresh > 0) {
                refreshTimeout = setTimeout(refreshConnection, msUntilRefresh);
              }
            }

            ws.onopen = () => {
              if (ws !== websocket) return;
              ws.send(JSON.stringify({ type: 'connection_init' }));
            };

            ws.onmessage = async (event) => {
              if (ws !== websocket) return;
              const validation = validateWebSocketMessage(event.data);
              if (!validation.isValid || !validation.message) return;

              const { message } = validation;

              if (message.type === 'connection_ack') {
                resolve();
              } else if (message.type === 'data' && message.id) {
                // Route data to the correct subscription observer by ID
                const sub = subscriptions.get(message.id as string);
                const payload = message.payload as { data?: unknown };
                if (sub?.observer.next && !isNil(payload?.data)) {
                  // Create a safe copy of the data to prevent prototype pollution
                  const safeData = JSON.parse(JSON.stringify(payload.data));
                  sub.observer.next({ data: safeData });
                }
              } else if (message.type === 'error' && message.id) {
                const sub = subscriptions.get(message.id as string);
                sub?.observer.error?.(new Error('WebSocket subscription error occurred'));
              } else if (message.type === 'connection_error') {
                console.error('WebSocket connection error occurred');
                teardown(new Error('WebSocket connection error occurred'));
                reject(new Error('WebSocket connection error occurred'));
              }
              // Silently ignore other message types like 'ka' (keep-alive), 'start_ack', etc.
            };

            ws.onerror = () => {
              if (ws !== websocket) return;
              console.error('WebSocket connection error occurred');
              teardown(new Error('WebSocket connection error occurred'));
              reject(new Error('WebSocket connection error occurred'));
            };

            ws.onclose = (event) => {
              console.debug('WebSocket closed');
              if (ws === websocket) {
                // Unexpected close — not initiated by teardown()
                teardown(new Error(`WebSocket closed unexpectedly (code: ${event.code})`));
              }
            };
          } catch (error) {
            console.error('WebSocket setup error:', error);
            const sanitizedError = error instanceof Error ? error : new Error('WebSocket setup failed');
            teardown(sanitizedError);
            reject(sanitizedError);
          }
        })();
      });

      return connectionReady;
    };

    /**
     * Sends the 'start' message for a subscription over the shared connection.
     * Signs the request with cross-account credentials and includes custom headers.
     */
    const registerSubscription = async (
      id: string,
      options: { query: string; variables?: Record<string, unknown> },
      observer: { next?: (value: { data: unknown }) => void; error?: (error: Error) => void },
    ) => {
      subscriptions.set(id, { observer, options });

      try {
        await connect();
        if (!subscriptions.has(id)) return; // unsubscribed while connecting

        const headers = await customHeaders({
          queryString: options.query,
          url: aurigaSettings.apiParams.deckManagementGraphqlEndpoint,
        });
        if (!subscriptions.has(id)) return; // unsubscribed while fetching headers

        const subscriptionMessage = await createSubscriptionMessage(options, headers, aurigaSettings);
        if (!subscriptions.has(id)) return; // unsubscribed while creating message
        // Override the random ID with our tracked subscription ID
        (subscriptionMessage as { id: string }).id = id;

        // Validate connection is still open before sending
        if (websocket?.readyState === WebSocket.OPEN) {
          console.debug('[WebSocket] Sending subscription start:', { id, query: options.query.slice(0, 60) });
          websocket.send(JSON.stringify(subscriptionMessage));
        } else {
          throw new Error('WebSocket not open');
        }
      } catch (error) {
        if (subscriptions.has(id)) {
          subscriptions.delete(id);
          console.error('Failed to register subscription:', error);
          observer.error?.(error instanceof Error ? error : new Error('Failed to register subscription'));
        }
      }
    };

    /** Refreshes the WebSocket connection by tearing down and re-registering all active subscriptions */
    const refreshConnection = () => {
      console.debug('Refreshing WebSocket credentials...');
      const active = new Map(subscriptions);
      teardown();
      subscriptions.clear();
      active.forEach(({ observer, options }, id) => {
        registerSubscription(id, options, observer);
      });
    };

    return {
      graphql: (options: { query: string; variables?: Record<string, unknown> }) => {
        const isSubscription = options.query?.trim().toLowerCase().startsWith('subscription');

        return {
          subscribe: (observer: { next?: (value: { data: unknown }) => void; error?: (error: Error) => void }) => {
            if (!isSubscription) {
              console.error('Non-subscription query used with subscription client');
              observer.error?.(new Error('Non-subscription query used with subscription client'));
              return { unsubscribe: () => {} };
            }

            const id = crypto.randomUUID();

            // Begin WebSocket setup (or reuse existing connection) for this subscription
            registerSubscription(id, options, observer);

            return {
              unsubscribe: () => {
                subscriptions.delete(id);
                // Send stop message if connection is still open
                if (websocket?.readyState === WebSocket.OPEN) {
                  websocket.send(JSON.stringify({ id, type: 'stop' }));
                }
                // Close connection when no active subscriptions remain
                if (subscriptions.size === 0) {
                  teardown();
                }
              },
            };
          },
        };
      },
    };
  };

  // Creates the Deck event tracking Subscription client
  const deckManagementSubscriptionClient = createAurigaSubscriptionClient();

  // Persist the generated api clients
  apiClients = { dimSumClient, deckManagementClient, deckManagementSubscriptionClient };

  return apiClients;
};

export default configureApiClients;
